/**
 * «Bu sertifikayla gönderilecek e-posta» — the one editing surface, used from two places.
 *
 * The preparation wizard's step and the editor's tab render THIS component against THIS content.
 * There is no second editor and no second copy of the message: what the wizard carries is what the
 * editor opens, and both save through the same port.
 *
 * ONE MESSAGE, TWO WAYS IN
 * ------------------------
 * There used to be two bodies — a plain-text one and an HTML one — and a control asking which of
 * them to send. That is two e-mails for the author to keep in step, and a silent way for the wrong
 * one to go out. There is now one source, the HTML: «Metin» edits it with the marks an e-mail uses,
 * «HTML» edits it as code, and the plain-text alternative recipients receive is derived by the
 * server from the very document it sends. Switching tabs changes nothing and saves nothing.
 *
 * WHEN THE SIMPLE TAB STEPS BACK
 * ------------------------------
 * A design built from tables and images is a layout, and a rich-text box would let one keystroke
 * dissolve it. So the «Metin» tab shows such a message instead of editing it, says where to
 * continue, and keeps the source exactly as it is.
 *
 * BEFORE THE CERTIFICATE EXISTS
 * -----------------------------
 * The wizard's step has no template id, and that used to mean no standard message, no copyable
 * HTML and no preview. It now asks the server to compose them from what the wizard has typed —
 * which creates no template, no record and no claim link — so the author does the work where they
 * asked to do it.
 *
 * NOTHING HERE SENDS. Copying, previewing and saving touch no queue and mint no claim token; the
 * preview's values are a visible sample and are labelled as one.
 */
import { type ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  CertificateEmailContext,
  CertificateEmailDraft,
  CertificateEmailPreview,
  CertificateEmailView,
  TemplateEmailPort,
} from '../../data/template-email';
import {
  CERTIFICATE_LINK,
  hasCertificateLinkTarget,
  isSimpleEmailHtml,
  withBodyFragment,
} from '../../lib/email-html';
import { forgetPendingEmail, pendingEmail, rememberPendingEmail } from '../../lib/pending-email';
import { RichEmailEditor } from './RichEmailEditor';

export interface CertificateEmailPanelProps {
  /** The port, or null on a surface with no server behind it. */
  port: TemplateEmailPort | null;
  /** The certificate whose e-mail this is, or null while the wizard has not created it yet. */
  templateId: string | null;
  /** What the wizard has typed so far, so the server can compose the right standard message. */
  context?: CertificateEmailContext | null;
  /** The wizard's carried draft, and the way back to it. Absent in the editor. */
  draft?: CertificateEmailDraft | null;
  onDraftChange?: (draft: CertificateEmailDraft | null) => void;
  /**
   * The panel's own heading. The wizard passes its step number; the editor's dialog passes `null`,
   * because its title bar already says the same sentence and printing it twice reads as a mistake.
   */
  heading?: string | null;
}

const EMPTY_DRAFT: CertificateEmailDraft = {
  subject: '',
  bodyText: '',
  bodyHtml: null,
  format: 'html',
};

const PLACEHOLDER_LABELS: Record<string, string> = {
  holder_name: 'Alıcının adı',
  credential_title: 'Sertifikanın adı',
  credential_code: 'Doğrulama kodu',
  credential_url: 'Herkese açık doğrulama sayfası',
  certificate_link: 'Kişiye özel sertifika bağlantısı',
};

/** The clipboard, with an honest failure: a browser that refuses is told about, not worked around. */
async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/** One content's identity, for «is the preview on screen still of this?» and nothing else. */
const keyOf = (d: CertificateEmailDraft): string =>
  JSON.stringify([d.subject, d.bodyHtml, d.bodyText, d.format]);

/** What the server calls each part of the message, in the words the screen uses for it. */
const FIELD_LABELS: Record<string, string> = {
  subject: 'Konu',
  bodyHtml: 'HTML gövdesi',
  bodyText: 'Metin gövdesi',
};

export interface EmailProblem {
  key: string;
  code: string;
  message: string;
}

/**
 * The refusal, as the server actually explained it.
 *
 * «E-posta içeriği geçerli değil» is the envelope's summary, and on its own it sends the author
 * back to a page of HTML with no idea which line offended. The answer already carries `details`
 * — field, code, sentence — and this reads them without knowing which client threw: anything with
 * a `details` array of the right shape is read, so the panel behaves the same under the product's
 * `ApiError` and under a test double.
 */
function problemsOf(error: unknown): EmailProblem[] {
  const raw = (error as { details?: unknown } | null)?.details;
  if (!Array.isArray(raw)) return [];
  const out: EmailProblem[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const { key, code, message } = item as Record<string, unknown>;
    if (typeof message !== 'string' || message === '') continue;
    out.push({
      key: typeof key === 'string' ? key : '',
      code: typeof code === 'string' ? code : '',
      message,
    });
  }
  return out;
}

const sameDraft = (a: CertificateEmailDraft, b: CertificateEmailDraft): boolean =>
  a.subject === b.subject && a.bodyHtml === b.bodyHtml && a.bodyText === b.bodyText;

export function CertificateEmailPanel({
  port,
  templateId,
  context,
  draft,
  onDraftChange,
  heading,
}: CertificateEmailPanelProps): ReactElement {
  const [view, setView] = useState<CertificateEmailView | null>(null);
  /**
   * What the wizard was carrying when this step opened, and therefore what must not be lost.
   *
   * The wizard keeps the step's work in its own store and hands it back on the way in. It used to
   * be taken as the opening value and then overwritten by the standard message the load fetched a
   * moment later — so an author who wrote their e-mail, went on to «Önizleme» and came back found
   * the product's own text in the box and theirs gone. Read once, on mount: it is a starting
   * value, not a prop this component follows.
   */
  const carried = useRef<CertificateEmailDraft | null>(templateId === null ? (draft ?? null) : null);
  const [tab, setTab] = useState<'text' | 'html'>(carried.current ? 'html' : 'text');
  const [local, setLocal] = useState<CertificateEmailDraft>(carried.current ?? EMPTY_DRAFT);
  /* An unsaved change from the first render when there is carried work: that is what it is. */
  const [dirty, setDirty] = useState(carried.current !== null);
  const [busy, setBusy] = useState(false);
  /**
   * Previewing has its own flag, and the button stays pressable while it runs.
   *
   * A preview reads: it stores nothing, queues nothing and mints no token, so a second press is not
   * a second act — it is «no, THIS one». Locking the button would make the author wait on an answer
   * they have already stopped wanting, so the newer request simply supersedes the older one, and
   * the ticket below is what makes that safe.
   */
  const [previewing, setPreviewing] = useState(false);
  const [message, setMessage] = useState<{ kind: 'ok' | 'warn' | 'error'; text: string } | null>(null);
  const [notes, setNotes] = useState<string[]>([]);
  const [preview, setPreview] = useState<{ of: string; result: CertificateEmailPreview } | null>(null);
  const [confirmReset, setConfirmReset] = useState(false);
  /** The field-level refusal the server sent with its last «no», or nothing. */
  const [problems, setProblems] = useState<EmailProblem[]>([]);
  /**
   * The last source this product's SERVER produced, and therefore the only markup the «Metin» tab
   * may be mounted over.
   *
   * The simple tab is an editing host inside the console's own page: whatever is written into it
   * runs there, and an author who pastes `<p onclick="…">` into the HTML tab must not be able to
   * hand that to the panel by switching tabs. So the host is offered only for markup that came
   * back from the server's cleaning call — a load, a save, or a preview — and for what the host
   * itself then makes of it, which the browser writes rather than the author.
   */
  const [cleanHtml, setCleanHtml] = useState<string | null>(null);
  /**
   * Which read is allowed to land.
   *
   * NOT a «loaded once» flag and NOT a cleanup that cancels. Both were tried and both were wrong:
   * React mounts an effect twice in development, so a flag set by the first pass made the second
   * pass skip the read, while the first pass's result was thrown away by its own cleanup — the
   * panel then sat empty with «Kaydet» disabled for ever, which is exactly what the browser test
   * caught. A ticket says something narrower and true: apply the result of the read that is still
   * the current one, and ignore an older one that arrived late.
   */
  const currentLoad = useRef<object | null>(null);
  /** The same rule for previews, so a slow answer cannot paint over a newer one. */
  const currentPreview = useRef<object | null>(null);

  /** What a preview is a preview OF. A change to any of it makes the picture on screen old. */
  const contentKey = useMemo(() => keyOf(local), [local]);
  const previewStale = preview !== null && preview.of !== contentKey;

  const applyView = useCallback((v: CertificateEmailView) => {
    setView(v);
    setLocal({ subject: v.subject, bodyText: v.bodyText, bodyHtml: v.bodyHtml, format: v.format });
    setCleanHtml(v.bodyHtml);
    setDirty(false);
  }, []);

  /**
   * Whether there is unsaved work RIGHT NOW, readable from inside a promise that started earlier.
   *
   * A request in flight closed over the `dirty` of the render that started it, so an answer that
   * arrived after the author had begun typing still applied itself on top. The ticket said «is
   * this still the current request»; this says the other half — «has the author moved on since».
   */
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;
  /** The same, for the content itself: what is on screen now, not what was when a request left. */
  const localRef = useRef(local);
  localRef.current = local;

  const edit = useCallback((patch: Partial<CertificateEmailDraft>) => {
    setLocal((prev) => ({ ...prev, ...patch }));
    setDirty(true);
    setMessage(null);
  }, []);

  /**
   * Telling the wizard, and keeping the unsaved copy, AFTER the render rather than during it.
   *
   * Both of these are side effects, and they used to sit inside the state updater — which React
   * may call while it is rendering, so the wizard was asked to re-render in the middle of this
   * component's render and said so in the console. The updater is now pure and the reporting
   * happens here, once per real change; `reported` is what stops an inline `onDraftChange` prop
   * from turning every render into another round.
   */
  const reported = useRef<string>('');
  useEffect(() => {
    if (!dirty) return;
    const stamp = JSON.stringify(local);
    if (reported.current === stamp) return;
    reported.current = stamp;
    onDraftChange?.(local);
    /* Unsaved work survives the dialog being closed and opened again. */
    if (templateId) rememberPendingEmail(templateId, local);
  }, [dirty, local, onDraftChange, templateId]);

  /** The single source, edited from either tab. `format` follows it rather than being chosen. */
  /**
   * The single source, edited from either tab. `format` follows it rather than being chosen.
   *
   * `stillClean` says whether the new text is something the browser's own editing host produced
   * from already-clean markup. Typed HTML is not, so the simple tab steps back until the server
   * has seen it.
   */
  const setSource = useCallback(
    (html: string, stillClean = false) => {
      edit({ bodyHtml: html, format: 'html' });
      setCleanHtml(stillClean ? html : null);
    },
    [edit],
  );

  /**
   * The reusable HTML, with its placeholders intact.
   *
   * What is copied is the SOURCE, never the preview: the preview has a sample name and a sample
   * address resolved into it, and copying that would hand the author a message that says «Örnek
   * Alıcı» to everybody.
   */
  const copyHtml = useCallback(async () => {
    const source = local.bodyHtml ?? view?.standard.bodyHtml ?? '';
    if (!source) {
      setMessage({ kind: 'error', text: 'Kopyalanacak HTML yok.' });
      return;
    }
    setMessage(
      (await copyText(source))
        ? { kind: 'ok', text: 'HTML kodu kopyalandı. Değişkenleri ve bağlantı işaretini koruyun.' }
        : { kind: 'error', text: 'Panoya kopyalanamadı; kodu seçip elle kopyalayabilirsiniz.' },
    );
  }, [local.bodyHtml, view]);

  const copyLinkPlaceholder = useCallback(async () => {
    setMessage(
      (await copyText(CERTIFICATE_LINK))
        ? { kind: 'ok', text: 'Bağlantı alanı kopyalandı.' }
        : { kind: 'error', text: 'Panoya kopyalanamadı.' },
    );
  }, []);

  const runPreview = useCallback(
    async (content: CertificateEmailDraft, key: string, extraNotes: string[] = []) => {
      if (!port) return null;
      const ticket = {};
      currentPreview.current = ticket;
      setPreviewing(true);
      try {
        const p = templateId
          ? await port.preview(templateId, content)
          : context && port.draft
            ? (await port.draft(context, content)).preview
            : null;
        /* An answer that is no longer the current request is dropped, not painted over the newer
           one — otherwise the slower of two presses wins, which is the wrong one. */
        if (!p || currentPreview.current !== ticket) return null;
        setPreview({ of: key, result: p });
        setProblems([]);
        /* The preview came back from the same cleaning call, so what it cleaned is now known
           clean — which is what lets the «Metin» tab open over HTML that was just typed. */
        if (p.cleanedHtml !== null && p.cleanedHtml === content.bodyHtml) setCleanHtml(p.cleanedHtml);
        setNotes([...new Set([...extraNotes, ...p.notes])]);
        return p;
      } catch (e) {
        if (currentPreview.current === ticket) {
          setProblems(problemsOf(e));
          setPreview(null);
          setMessage({ kind: 'error', text: `Önizleme alınamadı: ${(e as Error).message}` });
        }
        return null;
      } finally {
        if (currentPreview.current === ticket) setPreviewing(false);
      }
    },
    [port, templateId, context],
  );

  /**
   * What the screen opens with: the server's content, unless there is unsaved work to keep.
   *
   * Three things land here and they are not the same thing. The SERVER's content is the starting
   * point. The wizard's CARRIED work and a save that never landed (`pendingEmail`) are the
   * author's own, so they stay on top of it — and because they have not been through the cleaning
   * call, they are previewed rather than trusted: the «Metin» tab reopens over them only if the
   * server answers that what it cleaned is what was sent.
   *
   * The guard is `dirtyRef`, not `dirty`: this promise was started by an earlier render, and an
   * author who typed while it was in flight must not have their words replaced by an answer to a
   * question they have already moved past.
   */
  useEffect(() => {
    if (!port) return;
    const ticket = {};
    currentLoad.current = ticket;
    setBusy(true);
    const landing = templateId
      ? port.read(templateId).then((v) => ({ v, p: null as CertificateEmailPreview | null }))
      : context && port.draft
        ? port.draft(context, null).then((r) => ({ v: r.view, p: r.preview }))
        : Promise.resolve(null);
    landing
      .then((got) => {
        if (currentLoad.current !== ticket || !got) return;
        setView(got.v);
        /*
         * Work that never reached the server comes back on top of it.
         *
         * The wizard saves the e-mail against the certificate it has just created, and when that
         * second request failed the wizard used to reset anyway — the certificate existed and the
         * message did not. Now it is kept for this tab and restored here, as an unsaved change, so
         * «Kaydet» writes it to the SAME certificate rather than the author writing it again.
         */
        const pending = templateId ? pendingEmail(templateId) : undefined;
        if (pending) {
          setLocal(pending);
          setCleanHtml(null);
          setDirty(true);
          setTab(isSimpleEmailHtml(pending.bodyHtml) ? 'text' : 'html');
          setMessage({
            kind: 'warn',
            text: 'Kaydedilmemiş e-posta içeriğiniz geri yüklendi. «Kaydet» ile bu sertifikaya yazın.',
          });
        } else if (!dirtyRef.current) {
          /* Nothing of the author's to protect: the standard message for THIS context is shown. */
          applyView(got.v);
          setTab(isSimpleEmailHtml(got.v.bodyHtml) ? 'text' : 'html');
        }
        /* Unsaved work is shown as it is, and then checked — which is what makes it previewable. */
        const unsaved = pending ?? (dirtyRef.current ? localRef.current : null);
        if (unsaved) void runPreview(unsaved, keyOf(unsaved));
      })
      .catch((e: unknown) => {
        if (currentLoad.current === ticket)
          setMessage({ kind: 'error', text: `E-posta okunamadı: ${(e as Error).message}` });
      })
      .finally(() => {
        if (currentLoad.current === ticket) setBusy(false);
      });
    /* `local` is read through its ref rather than named as a dependency: depending on it would
       restart the read on every keystroke, which is the loop this panel already learned once. */
  }, [port, templateId, context, applyView, runPreview]);

  const onPreview = useCallback(() => {
    void runPreview(local, contentKey);
  }, [runPreview, local, contentKey]);

  const save = useCallback(async () => {
    if (!port || !templateId) return;
    setBusy(true);
    setMessage(null);
    const sent = keyOf(local);
    try {
      const saved = await port.save(templateId, local);
      const next: CertificateEmailDraft = {
        subject: saved.subject,
        bodyText: saved.bodyText,
        bodyHtml: saved.bodyHtml,
        format: saved.format,
      };
      setView(saved);
      setNotes(saved.sanitized.notes);
      setProblems([]);
      /*
       * A save answers the content it was GIVEN, which may no longer be the content on screen.
       *
       * Writing the answer over a newer edit loses words the author typed while the request was in
       * flight — and worse, it looks like the product decided their change did not happen. What was
       * stored is said plainly instead, and what they have typed since stays where it is, unsaved.
       */
      if (keyOf(localRef.current) !== sent) {
        setMessage({
          kind: 'warn',
          text: 'Kaydedildi. Bu sırada yaptığınız değişiklikler henüz kaydedilmedi — «Kaydet» ile yazabilirsiniz.',
        });
        return;
      }
      setLocal(next);
      setCleanHtml(saved.bodyHtml);
      setDirty(false);
      forgetPendingEmail(templateId);
      if (saved.sanitized.changed) {
        /*
         * «Önizlemede sonucu görüyorsunuz» has to be true when it is said.
         *
         * Cleaning changed the design, so the picture on screen is of something that no longer
         * exists. The preview is re-run against what was actually stored before the sentence is
         * shown, rather than pointing at a stale frame.
         */
        await runPreview(
          next,
          JSON.stringify([next.subject, next.bodyHtml, next.bodyText, next.format]),
          saved.sanitized.notes,
        );
        setMessage({
          kind: 'warn',
          text: 'Kaydedildi. Desteklenmeyen bazı tasarım özellikleri kaldırıldı; önizlemede kaydedilen sonucu görüyorsunuz.',
        });
      } else {
        setMessage({ kind: 'ok', text: 'E-posta kaydedildi.' });
      }
    } catch (e) {
      /*
       * A refusal names its reason, and the picture beside it stops claiming to be this content.
       *
       * The author is looking at HTML they just pasted and a preview of something else; leaving
       * that preview up while the save fails is the screen telling them their design was accepted.
       * It comes down until a preview of THIS content succeeds. What they pasted stays where it is.
       */
      setProblems(problemsOf(e));
      setPreview(null);
      setMessage({ kind: 'error', text: `Kaydedilemedi: ${(e as Error).message}` });
    } finally {
      setBusy(false);
    }
  }, [port, templateId, local, runPreview]);

  const doReset = useCallback(async () => {
    if (!port || !templateId) return;
    setBusy(true);
    setConfirmReset(false);
    try {
      const v = await port.reset(templateId);
      applyView(v);
      setTab(isSimpleEmailHtml(v.bodyHtml) ? 'text' : 'html');
      forgetPendingEmail(templateId);
      setPreview(null);
      setMessage({ kind: 'ok', text: 'Standart e-postaya dönüldü.' });
    } catch (e) {
      setMessage({ kind: 'error', text: `Dönülemedi: ${(e as Error).message}` });
    } finally {
      setBusy(false);
    }
  }, [port, templateId, applyView]);

  /** «Standart e-postaya dön» also undoes an unsaved edit, which is the other way back. */
  const resetToStandard = useCallback(() => {
    if (!view) return;
    if (!view.custom) {
      const standard: CertificateEmailDraft = {
        subject: view.standard.subject,
        bodyText: view.standard.bodyText,
        bodyHtml: view.standard.bodyHtml,
        format: 'html',
      };
      setLocal(standard);
      setDirty(false);
      reported.current = JSON.stringify(standard);
      carried.current = null;
      if (templateId) forgetPendingEmail(templateId);
      /*
       * `null`, not the standard text: «standart e-postaya dön» means the author has nothing of
       * their own here any more. Handing the wizard a copy of today's standard message would make
       * it look like an edit — carried back on the next visit, and frozen against the title or the
       * policy changing in an earlier step, which is exactly what «standart» must not mean.
       */
      onDraftChange?.(null);
      setConfirmReset(false);
      setMessage({ kind: 'ok', text: 'Kaydedilmemiş değişiklikler geri alındı.' });
      return;
    }
    void doReset();
  }, [view, templateId, onDraftChange, doReset]);

  const placeholders = view?.placeholders ?? Object.keys(PLACEHOLDER_LABELS);
  const simple = isSimpleEmailHtml(local.bodyHtml);
  const serverClean = local.bodyHtml === null || local.bodyHtml === cleanHtml;
  const canReset =
    !busy &&
    view !== null &&
    (view.custom || (dirty && !sameDraft(local, { ...EMPTY_DRAFT, ...view.standard, format: 'html' })));
  const linkMissing = local.bodyHtml !== null && !hasCertificateLinkTarget(local.bodyHtml);

  return (
    <section className="stack" data-testid="certificate-email-panel">
      <header className="stack" style={{ gap: '4px' }}>
        {heading === null ? null : (
          <h2 style={{ margin: 0 }}>{heading ?? 'Bu sertifikayla gönderilecek e-posta'}</h2>
        )}
        <p className="muted" style={{ margin: 0 }}>
          E-postayı bir kez hazırlayın. Alıcı bilgileri ve sertifika bağlantısı her kişiye otomatik
          yerleştirilir.
        </p>
      </header>

      {port === null ? (
        <p className="warn-box" role="status">
          Bu yüzeyde e-posta düzenlenemez; sertifika editöründen açın.
        </p>
      ) : null}

      {templateId === null && port !== null ? (
        <p className="muted" data-testid="email-wizard-note">
          Sertifika henüz oluşturulmadı. Buradaki e-posta, sertifikayla birlikte kaydedilir.
        </p>
      ) : null}

      <div className="email-editor" data-testid="email-editor-grid">
        <div className="stack">
          <label className="field">
            <span>Konu</span>
            <input
              data-testid="email-subject"
              value={local.subject}
              onChange={(e) => edit({ subject: e.target.value })}
            />
          </label>

          <div className="tabs" role="tablist" aria-label="E-posta gövdesi">
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'text'}
              data-testid="email-tab-text"
              className={tab === 'text' ? 'active' : ''}
              onClick={() => setTab('text')}
            >
              METİN
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'html'}
              data-testid="email-tab-html"
              className={tab === 'html' ? 'active' : ''}
              onClick={() => setTab('html')}
            >
              HTML
            </button>
          </div>

          {tab === 'text' ? (
            simple && serverClean ? (
              <RichEmailEditor
                source={cleanHtml}
                onChange={(fragment) => setSource(withBodyFragment(local.bodyHtml, fragment), true)}
                disabled={busy}
              />
            ) : (
              <div className="stack" style={{ gap: '6px' }} data-testid="email-layout-notice">
                <p className="warn-box" style={{ margin: 0 }}>
                  {simple ? (
                    <>
                      Bu içerik henüz sunucuda denetlenmedi; basit düzenleyici yalnız denetlenmiş içerik
                      üzerinde açılır. Aşağıda olduğu gibi görüyorsunuz —<strong> Önizle </strong>
                      dedikten sonra burada düzenleyebilir, dilerseniz<strong> HTML </strong>
                      sekmesinden devam edebilirsiniz.
                    </>
                  ) : (
                    <>
                      Bu e-posta tablo ve görsellerden oluşan bir tasarım. Basit düzenleyici onu bozmamak için
                      devre dışı: içeriği aşağıda olduğu gibi görüyorsunuz, düzenlemeye
                      <strong> HTML </strong> sekmesinden devam edin.
                    </>
                  )}
                </p>
                <iframe
                  title="E-posta tasarımı"
                  data-testid="email-layout-frame"
                  sandbox=""
                  srcDoc={local.bodyHtml ?? ''}
                  style={{ width: '100%', minHeight: '260px', border: '1px solid #ddd', background: '#fff' }}
                />
              </div>
            )
          ) : (
            <label className="field">
              <span>HTML gövdesi</span>
              <textarea
                data-testid="email-body-html"
                rows={14}
                spellCheck={false}
                value={local.bodyHtml ?? ''}
                onChange={(e) => setSource(e.target.value)}
              />
            </label>
          )}

          {linkMissing ? (
            <p className="warn-box" style={{ margin: 0 }} data-testid="email-link-missing">
              Bu e-postada sertifika bağlantısı yok. Bir bağlantının adresi <code>{CERTIFICATE_LINK}</code>{' '}
              olmalı; aksi hâlde kaydedilemez.
            </p>
          ) : null}

          <div className="row" style={{ gap: '8px', flexWrap: 'wrap' }}>
            <button type="button" data-testid="email-copy-html" onClick={copyHtml} disabled={busy}>
              HTML kodunu kopyala
            </button>
            <button type="button" data-testid="email-copy-link" onClick={copyLinkPlaceholder}>
              Bağlantı alanını kopyala
            </button>
            <button type="button" data-testid="email-preview" onClick={onPreview}>
              {previewing ? 'Önizleniyor…' : 'Önizle'}
            </button>
            <button
              type="button"
              className="primary"
              data-testid="email-save"
              onClick={save}
              disabled={busy || templateId === null || !dirty}
            >
              Kaydet
            </button>
            <button
              type="button"
              data-testid="email-reset"
              onClick={() => setConfirmReset(true)}
              disabled={!canReset}
            >
              Standart e-postaya dön
            </button>
          </div>

          {confirmReset ? (
            <div className="warn-box" role="alertdialog" data-testid="email-reset-confirm">
              <p style={{ marginTop: 0 }}>
                {view?.custom
                  ? 'Bu sertifikanın özel e-postası silinecek ve standart e-postaya dönülecek. Devam edilsin mi?'
                  : 'Kaydedilmemiş değişiklikleriniz geri alınacak ve standart e-postaya dönülecek. Devam edilsin mi?'}
              </p>
              <div className="row" style={{ gap: '8px' }}>
                <button type="button" data-testid="email-reset-confirm-yes" onClick={resetToStandard}>
                  Evet, standart e-postaya dön
                </button>
                <button type="button" onClick={() => setConfirmReset(false)}>
                  Vazgeç
                </button>
              </div>
            </div>
          ) : null}

          {message ? (
            <p
              className={message.kind === 'error' || message.kind === 'warn' ? 'warn-box' : 'muted'}
              role="status"
              data-testid="email-message"
            >
              {message.text}
            </p>
          ) : null}

          {problems.length > 0 ? (
            <div className="warn-box" data-testid="email-problems">
              <b>Kaydetmeyi engelleyen sorunlar</b>
              <ul style={{ margin: '6px 0 0 18px' }}>
                {problems.map((p) => (
                  <li key={`${p.key}:${p.code}:${p.message}`} data-problem={p.code}>
                    <b>{FIELD_LABELS[p.key] ?? p.key}:</b> {p.message}
                  </li>
                ))}
              </ul>
              <p className="small" style={{ margin: '6px 0 0' }}>
                Bunlar düzeltilene kadar kaydedilemez. Aşağıdaki liste ise desteklenmeyen tasarım
                özellikleridir ve kaydetmeyi engellemez.
              </p>
            </div>
          ) : null}

          {notes.length > 0 ? (
            <p className="small muted" style={{ margin: 0 }} data-testid="email-notes-heading">
              Desteklenmeyen tasarım özellikleri — kaydetmeyi engellemez:
            </p>
          ) : null}
          {notes.length > 0 ? (
            <ul className="muted" data-testid="email-notes">
              {notes.map((n) => (
                <li key={n}>{n}</li>
              ))}
            </ul>
          ) : null}

          <details data-testid="email-placeholders">
            <summary>Otomatik doldurulan alanlar</summary>
            <ul className="muted">
              {placeholders.map((p) => (
                <li key={p}>
                  <code>{`{{${p}}}`}</code> — {PLACEHOLDER_LABELS[p] ?? p}
                </li>
              ))}
            </ul>
          </details>
        </div>

        <div className="stack" data-testid="email-preview-pane">
          <h3 style={{ margin: 0 }}>Önizleme</h3>
          {preview ? (
            <>
              {previewStale ? (
                <p className="warn-box" style={{ margin: 0 }} data-testid="email-preview-stale">
                  İçerik bu önizlemeden sonra değişti. Güncel hâli için yeniden «Önizle» deyin.
                </p>
              ) : null}
              <p className="muted" style={{ margin: 0 }} data-testid="email-preview-sample-note">
                Örnek değerlerle gösteriliyor: {preview.result.sample.holder_name} · bağlantı örnektir, gerçek
                tek kullanımlık adres değildir.
              </p>
              <p style={{ margin: 0 }}>
                <b>Konu:</b> <span data-testid="email-preview-subject">{preview.result.subject}</span>
              </p>
              {/*
               * The preview is rendered in an isolated frame with no scripting and no access to this
               * page: it is somebody else's markup, cleaned but still not ours, and a preview that
               * could read the panel's session would be a hole with a nice border around it.
               */}
              <iframe
                title="E-posta önizlemesi"
                data-testid="email-preview-frame"
                sandbox=""
                srcDoc={preview.result.html}
                style={{ width: '100%', minHeight: '360px', border: '1px solid #ddd', background: '#fff' }}
              />
              <details>
                <summary>Düz metin karşılığı</summary>
                <pre data-testid="email-preview-text" style={{ whiteSpace: 'pre-wrap' }}>
                  {preview.result.text}
                </pre>
              </details>
            </>
          ) : (
            <p className="muted" data-testid="email-preview-empty">
              «Önizle» ile bu e-postanın bir alıcıda nasıl görüneceğini görün.
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
